import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Box, Container } from '@mui/material';

import { CustomWaterLoading } from 'src/components';
import { CursorTablePagination } from 'src/components/streametric/cursor-table-pagination';
import { FilterItem } from 'src/components/streametric/filters-bar';
import { useAuthentication } from 'src/features/authentication/context';
import { useGetAllSiteAlarms } from 'src/hooks/apiHooks/useGetAllAlarms';
import { useGetAlarmTotalCountForSite } from 'src/hooks/apiHooks/useGetAlarmTotalCountForSite';
import { useCursorPagination } from 'src/hooks/useCursorPagination';
import { useResponsive } from 'src/hooks/useResponsive';

import { AlarmsList } from '../components/alarmsList/AlarmsList';
import { SiteAlarmFilters } from '../components/SiteAlarmFilters';
import { parseAlarmFilters } from '../utils/parseAlarmFilters';

const SiteAlarms = () => {
  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
  } = useAuthentication();

  const isMobile = useResponsive('down', 'md');
  const [filters, setFilters] = useState('');

  const {
    page,
    rowsPerPage,
    rowsPerPageRef,
    pagesRef,
    resetPagination,
    getPageCursor,
    changeRowsPerPage,
    setPage,
  } = useCursorPagination();

  const { alarms, isLoaded, getAllSiteAlarms, onAcknowledged, setAlarmData } = useGetAllSiteAlarms({
    siteId: siteId!,
    filters: filters,
    rowsPerPage: rowsPerPage,
  });
  const { alarmCount, isLoaded: isAlarmCountLoaded } = useGetAlarmTotalCountForSite({
    customerId: customerId!,
    filters: [filters, `siteIdList=${siteId}`].filter(Boolean).join('&'),
  });

  // The onFiltersChange doesn't capture change if the chips are being removed.
  useEffect(() => {
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const onFiltersChange = useCallback(
    (filter: FilterItem[]) => {
      pagesRef.current = {};

      setFilters(parseAlarmFilters(filter));
    },
    [pagesRef]
  );

  const onPageChange = (nextPage: number) => {
    // Because the filter is for a specific site the cursor for site id can be the current site ID.
    let skipToSiteId = siteId || '';
    const { skipTo, isBack } = getPageCursor(nextPage, alarms);

    if (isBack && nextPage === 0) {
      skipToSiteId = '';
    }

    getAllSiteAlarms({
      limit: rowsPerPageRef.current,
      skipTo: skipTo,
      skipToSiteId: skipToSiteId,
      siteId: siteId!,
      filters: filters,
    });
    setPage(nextPage);
  };

  const onRowsPerPageChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const limit = changeRowsPerPage(event);

    getAllSiteAlarms({
      limit,
      siteId: siteId!,
      filters: filters,
    });
  };

  const isReady = isLoaded && isAlarmCountLoaded;

  const paginationProps = {
    count: alarmCount?.total,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        paddingInline: '0px !important',
        display: 'flex',
        height: isMobile ? '100%' : 'calc(100% - 138px)',
        flexDirection: 'column',
      }}
    >
      <SiteAlarmFilters
        onChange={onFiltersChange}
        leading={isReady && <CursorTablePagination {...paginationProps} />}
      />

      {!isReady ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <CustomWaterLoading />
        </Box>
      ) : (
        <AlarmsList
          alarms={alarms}
          isLoaded={isLoaded}
          onAcknowledged={onAcknowledged}
          setAlarmData={setAlarmData}
        />
      )}

      {isReady && <CursorTablePagination {...paginationProps} sx={{ marginBottom: '90px' }} />}
    </Container>
  );
};

export default SiteAlarms;
